<template>
  <div class="d-flex align-items-center">
    <span
      v-if="label"
      class="agent-note-input__label"
    >{{ label }}</span>

    <!--
      Saved 1s after the last keystroke, or at once on blur (Enter blurs). Never disabled while saving:
      that would drop the focus mid-typing. A failed save keeps the text; the next edit or blur retries it.

      A textarea so long notes wrap: one line at rest, scrolled to the note's last line; two while editing.
      Native rather than b-form-textarea, which never goes below two rows.
    -->
    <textarea
      ref="field"
      :value="localValue"
      class="agent-note-input form-control custom-form-input--xs"
      :class="{ 'agent-note-input--expanded': isFocused }"
      :rows="isFocused ? 2 : 1"
      :placeholder="placeholder"
      :maxlength="maxLength"
      :title="savedNote || ''"
      aria-label="Agent note"
      :data-cy="dataCy"
      @input="onInput"
      @focus="isFocused = true"
      @blur="onBlur"
      @keydown.enter.prevent="$event.target.blur()"
    />

    <!-- Space kept while hidden, so nothing jumps when the tick shows -->
    <font-awesome-icon
      :icon="['fas', 'check']"
      class="agent-note-input__saved ml-2"
      :class="{ 'agent-note-input__saved--visible': isSavedTickVisible }"
      title="Note saved"
      :data-cy="`${dataCy}-saved`"
    />
  </div>
</template>

<script>
import CallDispositionAPI from '@/api/call-disposition.api'
import { getApiErrorMessage, showErrorMessage } from '@/notification-utils'

// Matches CallDisposition.agentNote VARCHAR(1000)
const AGENT_NOTE_MAX_LENGTH = 1000

const SAVE_DELAY_MS = 1000

const SAVED_TICK_MS = 1000

// How long after a save a reloaded note that differs from it is treated as stale
const STALE_RELOAD_GUARD_MS = 2000

/**
 * The buyer agent's free-text note on one call (Call Logs row, softphone wrap-up), stored through
 * `PUT /reporting/buyer/{accountId}/calls/agent-note`. Emits `saved` with the stored note (null when cleared).
 * No success toast: it would pop up at every pause in typing; the tick says it instead. Failures still toast.
 */
export default {
  name: 'AgentNoteInput',

  props: {
    accountId: {
      type: String,
      required: true
    },

    callId: {
      type: String,
      required: true
    },

    // The stored note
    value: {
      type: String,
      default: null
    },

    placeholder: {
      type: String,
      default: 'Add note'
    },

    // Shown before the field, e.g. "Note"; none when empty
    label: {
      type: String,
      default: ''
    },

    dataCy: {
      type: String,
      default: 'agent-note'
    }
  },

  data() {
    return {
      maxLength: AGENT_NOTE_MAX_LENGTH,
      savedNote: this.value || null,
      // What the field shows. Its own copy: a native textarea re-rendered out of focus is reset to its
      // bound value, which would wipe text not stored yet if it were bound to `value` directly.
      localValue: this.value || '',
      isSaving: false,
      isSavedTickVisible: false,
      isFocused: false
    }
  },

  watch: {
    // A reload brings the stored note
    value(value) {
      // A list load started before the last save landed still carries the old note: don't let it
      // blank the note just stored. A later load brings the saved one.
      if (Date.now() - this.lastSavedAt < STALE_RELOAD_GUARD_MS && (value || null) !== this.savedNote) {
        return
      }

      this.savedNote = value || null

      // Never over text being typed or still on its way to the server
      if (!this.isFocused && !this.saveTimer && !this.isSaving) {
        this.localValue = value || ''
        this.showLastLine()
      }
    }
  },

  mounted() {
    this.showLastLine()
  },

  created() {
    // Not reactive: the typed text waiting out the delay, and text typed while a save was in flight
    this.saveTimer = null
    this.pendingValue = null
    this.queuedValue = null
    this.savedTickTimer = null
    this.lastSavedAt = 0
  },

  beforeDestroy() {
    // Leaving the page, or the wrap-up ending, must not lose a note still waiting out the delay
    if (this.saveTimer) {
      this.flushSave(this.pendingValue)
    }

    clearTimeout(this.savedTickTimer)
  },

  methods: {
    onInput(event) {
      this.localValue = event.target.value
      this.scheduleSave(this.localValue)
    },

    onBlur(event) {
      this.isFocused = false
      this.showLastLine()

      return this.flushSave(event.target.value)
    },

    // Collapsed to one line, the line worth seeing is the note's end, where the agent left off
    showLastLine() {
      this.$nextTick(() => {
        const field = this.$refs.field

        if (field) {
          field.scrollTop = field.scrollHeight
        }
      })
    },

    scheduleSave(value) {
      this.cancelSave()

      this.pendingValue = value
      this.saveTimer = setTimeout(() => this.flushSave(value), SAVE_DELAY_MS)
    },

    cancelSave() {
      clearTimeout(this.saveTimer)

      this.saveTimer = null
      this.pendingValue = null
    },

    flushSave(value) {
      this.cancelSave()

      return this.save(value)
    },

    async save(value) {
      // Blank clears the note, same as the API
      const agentNote = value.trim() || null

      // One save at a time; the latest text typed meanwhile goes out once it lands
      if (this.isSaving) {
        this.queuedValue = value
        return
      }

      if (agentNote === this.savedNote) {
        return
      }

      this.isSaving = true

      try {
        await CallDispositionAPI.setAgentNote(this.accountId, { callId: this.callId, agentNote })

        this.savedNote = agentNote
        this.lastSavedAt = Date.now()
        this.$emit('saved', agentNote)
        this.showSavedTick()
      }
      catch (error) {
        showErrorMessage(getApiErrorMessage(error, 'Unable to save the note'))
      }
      finally {
        this.isSaving = false
      }

      if (this.queuedValue !== null) {
        const queuedValue = this.queuedValue

        this.queuedValue = null
        await this.save(queuedValue)
      }
    },

    // A second save within the second restarts the timer instead of stacking timers
    showSavedTick() {
      clearTimeout(this.savedTickTimer)

      this.isSavedTickVisible = true
      this.savedTickTimer = setTimeout(() => {
        this.isSavedTickVisible = false
      }, SAVED_TICK_MS)
    }
  }
}
</script>

<style lang="scss" scoped>
// Sized by its rows, not the fixed height of the input style it borrows
.agent-note-input {
  height: auto;
  min-height: 0;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  overflow-y: hidden;
  resize: none;
  // Room for the scrollbar is kept while collapsed too, so the text doesn't rewrap when focus brings it in
  scrollbar-gutter: stable;

  &--expanded {
    overflow-y: auto;
  }
}

.agent-note-input__label {
  flex-shrink: 0;
  margin-right: 0.625rem;
}

.agent-note-input__saved {
  flex-shrink: 0;
  color: $success;
  visibility: hidden;

  &--visible {
    visibility: visible;
  }
}
</style>
